'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useInterval } from '@mantine/hooks';

import { generateQrCodeDataUrl } from './generateQrCodeDataUrl';

/**
 * ### useQrCode
 *
 * @description
 * Custom hook for generating and managing QR codes from URLs.
 * Supports both async URL fetching (e.g., from API) and sync URL generation (e.g., from utilities).
 * Automatically refreshes the QR code at a specified interval.
 *
 * @param getUrl - Function that returns a URL string or Promise<string>
 * @param options - Configuration options
 * @param options.refreshInterval - Interval in milliseconds to refresh the QR code (0 to disable). Default: 60000 (60 seconds)
 * @param options.enabled - Whether the hook is enabled. Default: true
 * @returns Object containing qrCode data URL, url, error message, and loading state
 */
export const useQrCode = (
  getUrl: () => string | Promise<string>,
  options: {
    refreshInterval?: number;
    enabled?: boolean;
  } = {},
) => {
  const { refreshInterval = 60 * 1000, enabled = true } = options;

  const [qrCode, setQrCode] = useState<string>('');
  const [url, setUrl] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const getUrlRef = useRef(getUrl);

  // Keep getUrl ref in sync to avoid stale closures
  useEffect(() => {
    getUrlRef.current = getUrl;
  }, [getUrl]);

  const generateQrCode = useCallback(async () => {
    if (!enabled) {
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      // Handle both sync (string) and async (Promise<string>) URL sources
      // Use ref to get latest getUrl function to avoid stale closures
      const urlResult = getUrlRef.current();
      const urlValue = typeof urlResult === 'string' ? urlResult : await urlResult;
      setUrl(urlValue);

      const qrData = await generateQrCodeDataUrl(urlValue);
      setQrCode(qrData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setQrCode('');
      setUrl('');
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  // Wrapper function to handle promise properly (errors are already handled in generateQrCode)
  const handleGenerateQrCode = useCallback(() => {
    generateQrCode().catch(() => {
      // Error is already handled in generateQrCode, catch here only to prevent unhandled promise rejection
    });
  }, [generateQrCode]);

  // Regenerate when enabled changes (getUrl is accessed via ref, so no need to include it in deps)
  useEffect(() => {
    handleGenerateQrCode();
  }, [handleGenerateQrCode]);

  const interval = useInterval(handleGenerateQrCode, refreshInterval);

  useEffect(() => {
    if (refreshInterval > 0 && enabled) {
      interval.start();
      return interval.stop;
    }
    return undefined;
  }, [interval, refreshInterval, enabled]);

  return {
    qrCode,
    url,
    error,
    isLoading,
    refetch: generateQrCode,
  };
};
